import PropTypes from "prop-types";
import EventMeta from "./EventMeta";
import UpcomingEvents from "./UpcomingEvents";
import BigTitleText from "../BigTitleText";
import LikeSection from "../utils/LikeSection.jsx";
import SafeHtmlContent from "../SafeHtmlContent";
import ShareLink from "../ShareLink.jsx";

const EventPost = ({ data }) => {
  const imageMainAltText = data ? `Bild för ${data.event.name}` : "";
  const likesData = data?.likes ?? data?.event?.likes;
  const upcomingEvents =
    data?.event?.related_events?.length > 0
      ? data.event.related_events
      : data?.event?.closest_upcoming_events || [];

  // Format date to a more readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("sv-SE", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  return (
    <>
      {data ? (
        <>
          <div className="grid sm:grid-cols-5 gap-8 sm:p-4 px-0 py-4">
            <div className="col-span-3 flex flex-col space-y-4">
              <div>
                <div>
                  <h1 className="text-4xl sm:text-5xl font-black text-white uppercase tracking-wider leading-tight drop-shadow-md mb-2">
                    {data.event.name}
                  </h1>
                </div>
                <div className="my-4 flex flex-col gap-3 bg-[#131313] border border-white/10 rounded-2xl p-4 shadow-md">
                  <div className="flex items-center gap-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-[#0047ff]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <div>
                      <p className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">
                        Datum
                      </p>
                      <p className="text-sm text-stone-200 font-semibold">{formatDate(data.event.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-[#0047ff]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <div>
                      <p className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">
                        Plats
                      </p>
                      <p className="text-sm text-stone-200 font-semibold">{data.event.location}</p>
                    </div>
                  </div>
                </div>
                <div className="mb-2">
                  {data.event.image_main && (
                    <div className="w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-2xl border border-white/10 shadow-lg">
                      <img
                        className="w-full h-full object-cover"
                        src={data.event.image_main}
                        alt={imageMainAltText}
                      />
                    </div>
                  )}
                </div>
                {data.event.description && (
                  <div className="prose shadow-lg max-w-none w-full my-4 bg-[#131313] border border-white/10 rounded-2xl p-6 overflow-hidden break-words text-stone-300 leading-relaxed text-sm sm:text-base">
                    <SafeHtmlContent
                      html={data.event.description}
                      className="prose prose-invert max-w-none break-words"
                    />
                  </div>
                )}
                <div className="flex items-center justify-between flex-wrap pr-2">
                  {likesData !== undefined && <LikeSection likes={likesData} />}
                  <div className="mt-4 mb-2 pt-4">
                    <ShareLink title={data.event.name} text="Dela event" />
                  </div>
                </div>
                <div className="shadow-md">
                  <EventMeta data={data} />
                </div>
              </div>
            </div>
            <div className="col-span-3 sm:col-span-2 flex flex-col sm:min-h-screen space-y-4">
              <h3 className="text-xl font-extrabold text-white uppercase tracking-wider pl-1 mb-4 mt-6">
                Fler evenemang
              </h3>
              <UpcomingEvents events={upcomingEvents} />
            </div>
          </div>
        </>
      ) : (
        <div>
          <h4>Laddar evenemang...</h4>
        </div>
      )}
    </>
  );
};

EventPost.propTypes = {
  data: PropTypes.shape({
    event: PropTypes.shape({
      name: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      location: PropTypes.string.isRequired,
      image_main: PropTypes.string,
      description: PropTypes.string,
      likes: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({
          count: PropTypes.number,
          user_has_liked: PropTypes.bool,
          object_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          object_type: PropTypes.string,
        }),
      ]),
      related_events: PropTypes.array,
      closest_upcoming_events: PropTypes.array,
    }).isRequired,
    likes: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.shape({
        count: PropTypes.number,
        user_has_liked: PropTypes.bool,
        object_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        object_type: PropTypes.string,
      }),
    ]),
    upcoming_events: PropTypes.array,
  }),
};

export default EventPost;
